import { z } from 'zod';

import type { Locale } from './travel_preferences';
import type { VoucherStatus } from './traveler_view';
import { isCancellationRequestedVoucher, isChangeRequestedVoucher } from './voucher_liveness';

/**
 * O nome do status de um voucher na tela: os 9 estados da VOLL antiga (`voll-enums`
 * VOUCHER.Status, os mesmos que o mapper do customer-api lê) mais
 * CANCELLATION_REQUESTED — emitido cujo cancelamento o viajante pediu e o legado
 * ainda não executou — e CHANGE_REQUESTED — emitido cujo voo o back office está
 * trocando (tag REQUESTED_CHANGE). O `VoucherStatus` de 6 grupos segue decidindo as regras
 * (voucher morto, prontidão, filtros); isto só nomeia.
 */
export const VoucherStatusNameSchema = z.enum([
  'REQUESTED',
  'WAITING_FOR_QUOTATION',
  'AWAITING_APPROVAL',
  'WAITING_FOR_EMISSION',
  'EMITTED',
  'CANCELED',
  'EXPIRED',
  'REPROVED',
  'REQUEST_DENIED',
  'CANCELLATION_REQUESTED',
  'CHANGE_REQUESTED',
]);
export type VoucherStatusName = z.infer<typeof VoucherStatusNameSchema>;

export interface VoucherStatusNameInput {
  /** O modal decide a alteração solicitada (só voo vira CHANGE_REQUESTED). */
  modal: string | null | undefined;
  status: VoucherStatus | null | undefined;
  internalStatus?: string | null;
  cancellationRequestedAt?: Date | null;
  tags?: readonly string[] | null;
}

/** Rede de segurança: sem o cru (ou com um estado que a VOLL antiga não tem), o nome
 *  mais próximo do grupo — a tela nunca mostra um código cru. */
const BY_GROUP: Record<VoucherStatus, VoucherStatusName> = {
  IN_PROGRESS: 'REQUESTED',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  CONFIRMED: 'EMITTED',
  CANCELLED: 'CANCELED',
  EXPIRED: 'EXPIRED',
  DECLINED: 'REPROVED',
};

/** O nome a exibir, lido direto do status cru da VOLL antiga. */
export function voucherStatusName(voucher: VoucherStatusNameInput): VoucherStatusName | null {
  if (isCancellationRequestedVoucher(voucher)) return 'CANCELLATION_REQUESTED';
  if (isChangeRequestedVoucher(voucher)) return 'CHANGE_REQUESTED';
  const raw = VoucherStatusNameSchema.safeParse(voucher.internalStatus?.trim().toUpperCase());
  if (raw.success && raw.data !== 'CANCELLATION_REQUESTED' && raw.data !== 'CHANGE_REQUESTED') {
    return raw.data;
  }
  return voucher.status ? BY_GROUP[voucher.status] : null;
}

/** pt-BR é a palavra da VOLL antiga (voucher-api `locales/pt`), só com a primeira
 *  letra maiúscula; en/es traduzem os mesmos estados. */
export const VOUCHER_STATUS_NAME_LABELS: Record<Locale, Record<VoucherStatusName, string>> = {
  'pt-BR': {
    REQUESTED: 'Solicitado',
    WAITING_FOR_QUOTATION: 'Aguardando cotação',
    AWAITING_APPROVAL: 'Aguardando aprovação',
    WAITING_FOR_EMISSION: 'Aguardando emissão',
    EMITTED: 'Emitido',
    CANCELED: 'Cancelado',
    EXPIRED: 'Expirado',
    REPROVED: 'Reprovado',
    REQUEST_DENIED: 'Cotação recusada',
    CANCELLATION_REQUESTED: 'Cancelamento solicitado',
    CHANGE_REQUESTED: 'Alteração em andamento',
  },
  en: {
    REQUESTED: 'Requested',
    WAITING_FOR_QUOTATION: 'Awaiting quote',
    AWAITING_APPROVAL: 'Awaiting approval',
    WAITING_FOR_EMISSION: 'Awaiting issuance',
    EMITTED: 'Issued',
    CANCELED: 'Cancelled',
    EXPIRED: 'Expired',
    REPROVED: 'Rejected',
    REQUEST_DENIED: 'Quote declined',
    CANCELLATION_REQUESTED: 'Cancellation requested',
    CHANGE_REQUESTED: 'Change in progress',
  },
  es: {
    REQUESTED: 'Solicitado',
    WAITING_FOR_QUOTATION: 'Esperando cotización',
    AWAITING_APPROVAL: 'Esperando aprobación',
    WAITING_FOR_EMISSION: 'Esperando emisión',
    EMITTED: 'Emitido',
    CANCELED: 'Cancelado',
    EXPIRED: 'Expirado',
    REPROVED: 'Rechazado',
    REQUEST_DENIED: 'Cotización rechazada',
    CANCELLATION_REQUESTED: 'Cancelación solicitada',
    CHANGE_REQUESTED: 'Cambio en curso',
  },
};
